import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { 
  User, Sun, Moon, FolderRoot as Football, LogOut, X, HelpCircle, Info, 
  Home, Users, DollarSign, Shuffle, MessageSquare, Bell, Crown, AlertCircle, Calendar,
  ArrowUpCircle, Clock, Clipboard
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { getProxiedImageUrl } from '../../utils/imageUtils';
import { useSupabase } from '../../contexts/SupabaseContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, isMobile = false }) => {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { supabase } = useSupabase();
  const { isTrialActive, isSubscribed, isLifetime, subscription } = useSubscriptionStatus();
  const { limits } = usePlanFeatures();
  const [profile, setProfile] = useState<any>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const navigate = useNavigate();

  // ✅ Calcula dias restantes a partir da data correta (trial ou plano)
  const calculateDaysLeft = (expirationDateStr?: string | null) => {
    if (!expirationDateStr) return 0;

    const now = new Date();
    const expirationDate = new Date(expirationDateStr);
    if (isNaN(expirationDate.getTime())) return 0;

    const diffTime = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // ✅ Usa trial_ends_at se estiver em trial, caso contrário plano_expira_em
  const daysLeft = calculateDaysLeft(
    isTrialActive ? profile?.trial_ends_at : profile?.plano_expira_em
  );

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        const { data } = await supabase
          .from('profiles')
          .select('id, nome, avatar_url, role, email, plano_expira_em, trial_ends_at, grupo_ativo, lifetime, updated_at')
          .eq('id', user.id)
          .single();

        if (data) {
          setProfile(data);
          setActiveGroupId(data.grupo_ativo);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchProfile();

    // Subscribe to profile changes for real-time updates
    const profileChannel = supabase
      .channel('profile_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user?.id}`
        },
        (payload) => {
          console.log('Profile updated, refreshing sidebar data...');
          setProfile(payload.new as any);
          if (payload.new.grupo_ativo !== undefined) {
            setActiveGroupId(payload.new.grupo_ativo);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(profileChannel);
    };
  }, [user, supabase]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during signout:', error);
    }
  };

  const handleProfileClick = () => {
    navigate('/perfil');
    onClose();
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) => 
    `flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
      isActive 
        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-light'
    }`;

  const getSubscriptionInfo = () => {
    if (isLifetime) {
      return {
        type: 'Lifetime',
        duration: 'Vitalício',
        days: null,
        color: 'bg-purple-500',
        textColor: 'text-purple-600 dark:text-purple-400',
        bgColor: 'bg-purple-100 dark:bg-purple-900/20',
        icon: <Crown className="w-5 h-5" />
      };
    }
    
    if (isSubscribed) {
      return {
        type: limits.planName,
        duration: `${limits.durationMonths} ${limits.durationMonths === 1 ? 'mês' : 'meses'}`,
        days: daysLeft,
        color: 'bg-primary-500',
        textColor: 'text-primary-600 dark:text-primary-400',
        bgColor: 'bg-primary-100 dark:bg-primary-900/20',
        icon: <Clock className="w-5 h-5" />
      };
    }
    
    if (isTrialActive) {
      return {
        type: 'Trial',
        duration: 'Período de avaliação',
        days: daysLeft,
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600 dark:text-yellow-400',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
        icon: <Clock className="w-5 h-5" />
      };
    }

    return {
      type: 'Expirado',
      duration: '',
      days: 0,
      color: 'bg-red-500',
      textColor: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
      icon: <AlertCircle className="w-5 h-5" />
    };
  };

  const subscriptionInfo = getSubscriptionInfo();

  const getProgressPercentage = () => {
    if (isLifetime || subscriptionInfo.days === null) return 100;
    
    // Get total days based on plan type
    const totalDays = limits.durationMonths * 30; // Convert months to approximate days
    
    return Math.min(100, Math.max(0, (daysLeft / totalDays) * 100));
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-paper">
      <div className="p-4 flex items-center justify-end">
        <button
          onClick={onClose}
          className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 flex flex-col">
        <div className="relative w-20 h-20 mb-3">
          <button
            onClick={handleProfileClick}
            className="w-20 h-20 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary-500"
            title="Editar perfil"
          >
            {profile?.avatar_url ? (
              <img
                src={`${getProxiedImageUrl(profile.avatar_url)}?v=${profile.updated_at ? new Date(profile.updated_at).getTime() : Date.now()}`}
                alt={profile?.nome || 'User'}
                className="w-full h-full object-cover"
                key={`${profile.avatar_url}-${profile.updated_at || Date.now()}`}
                onError={(e) => {
                  // Fallback if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.nome?.charAt(0) || 'U')}&background=random&color=fff&size=100`;
                }}
              />
            ) : (
              <div className="w-full h-full bg-primary-500 flex items-center justify-center text-3xl text-white font-bold">
                {profile?.nome?.charAt(0).toUpperCase() || <User className="w-10 h-10 text-white" />}
              </div>
            )}
          </button>
        </div>
        <div className="text-left">
          <p className="font-medium text-lg mb-1">{profile?.nome || 'Usuário'}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Desde {new Date(user?.created_at || '').toLocaleDateString('pt-BR')}
          </p>
        </div>

        <div className="mt-4 p-4 rounded-lg border dark:border-dark-border">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-2 rounded-lg ${subscriptionInfo.bgColor}`}>
              {subscriptionInfo.icon}
            </div>
            <div>
              <p className="font-medium">{subscriptionInfo.type}</p>
              {subscriptionInfo.duration && (
                <p className="text-sm text-gray-500">
                  {subscriptionInfo.duration}
                </p>
              )}
              {subscriptionInfo.days !== null && (
                <p className="text-sm text-gray-500">
                  {subscriptionInfo.days} {subscriptionInfo.days === 1 ? 'dia' : 'dias'} restantes
                </p>
              )}
            </div>
          </div>

          {(isTrialActive || isSubscribed) && !isLifetime && (
            <div className="mt-2">
              <div className="h-2 bg-gray-200 dark:bg-dark-light rounded-full overflow-hidden">
                <div
                  className={`h-full ${subscriptionInfo.color} transition-all duration-300`}
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
            </div>
          )}

          {isSubscribed && !isLifetime ? (
            <Link to="/pricing" className="btn-outline w-full mt-3 flex items-center justify-center gap-2 text-sm" onClick={onClose}>
              <X className="w-4 h-4" />
              Cancelar
            </Link>
          ) : !isLifetime && (
            <Link to="/pricing" className="btn-outline w-full mt-3 flex items-center justify-center gap-2 text-sm" onClick={onClose}>
              <ArrowUpCircle className="w-4 h-4" />
              {isTrialActive ? 'Fazer Upgrade' : 'Ver Planos'}
            </Link>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!isMobile && (
          <nav className="p-4 space-y-1">
            <NavLink to="/" className={navLinkClass} onClick={onClose} end>
              <Home className="w-5 h-5" />
              <span>Início</span>
            </NavLink>
            <NavLink to="/perfil" className={navLinkClass} onClick={onClose}>
              <User className="w-5 h-5" />
              <span>Meu Perfil</span>
            </NavLink>
            <NavLink to="/grupos" className={navLinkClass} onClick={onClose}>
              <Football className="w-5 h-5" />
              <span>Grupos</span>
            </NavLink>
            <NavLink to="/jogadores" className={navLinkClass} onClick={onClose}>
              <Users className="w-5 h-5" />
              <span>Jogadores</span>
            </NavLink>
            <NavLink to="/financeiro" className={navLinkClass} onClick={onClose}>
              <DollarSign className="w-5 h-5" />
              <span>Financeiro</span>
            </NavLink>
            <NavLink to="/sorteio" className={navLinkClass} onClick={onClose}>
              <Shuffle className="w-5 h-5" />
              <span>Sorteio</span>
            </NavLink>
            <NavLink to="/jogos" className={navLinkClass} onClick={onClose}>
              <Calendar className="w-5 h-5" />
              <span>Jogos</span>
            </NavLink>
            <NavLink to="/escalacao" className={navLinkClass} onClick={onClose}>
              <Clipboard className="w-5 h-5" />
              <span>Escalação</span>
            </NavLink>
            {limits.hasResenha && (
              <NavLink to="/resenha" className={navLinkClass} onClick={onClose}>
                <MessageSquare className="w-5 h-5" />
                <span>Resenha</span>
              </NavLink>
            )}
          </nav>
        )}

        <div className="p-4">
          <h3 className="px-4 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 mb-2">Configurações</h3>
          <nav className="space-y-1">
            <NavLink to="/grupos" className={navLinkClass} onClick={onClose}>
              <Football className="w-5 h-5" />
              <span>Grupos</span>
            </NavLink>
            <NavLink to="/lembretes" className={navLinkClass} onClick={onClose}>
              <Bell className="w-5 h-5" />
              <span>Lembretes</span>
            </NavLink>
            <NavLink to="/sobre" className={navLinkClass} onClick={onClose}>
              <Info className="w-5 h-5" />
              <span>Sobre</span>
            </NavLink>
            <NavLink to="/ajuda" className={navLinkClass} onClick={onClose}>
              <HelpCircle className="w-5 h-5" />
              <span>Ajuda</span>
            </NavLink>
            <button onClick={toggleTheme} className="flex items-center w-full px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-light transition-colors text-gray-600 dark:text-gray-300">
              {theme === 'dark' ? (
                <>
                  <Sun className="w-5 h-5 mr-2" />
                  <span>Modo Claro</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5 mr-2" />
                  <span>Modo Escuro</span>
                </>
              )}
            </button>
            <button onClick={handleSignOut} className="flex items-center w-full px-4 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
              <LogOut className="w-5 h-5 mr-2" />
              <span>Sair</span>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;